import React from 'react';
import { Label, Icon } from 'semantic-ui-react';

/**
 * Which stream a post goes into.
 *
 * Shown wherever somebody decides whether a post should go out — the review queue, the
 * scheduled list, and both detail screens. Since Phase 6 the stream *is* the audience, so a
 * moderator approving a post without seeing this is approving an unknown set of readers.
 * Community tags still appear next to it, but they now describe the post rather than decide
 * who receives it, and the two are easy to confuse — hence the explicit "Stream:" label and a
 * colour (blue) used for nothing else on these screens.
 *
 * `null` is a real answer, not an error: a post read from an organisation whose streams the
 * viewer cannot see resolves to nothing. It renders as plain text rather than a badge so it
 * does not read as a stream called "Unknown".
 */
export const StreamBadge: React.FC<{
    stream?: { id: number; name: string; isDefault?: boolean } | null;
    size?: 'mini' | 'tiny' | 'small';
    showLabel?: boolean;
}> = ({ stream, size = 'mini', showLabel = true }) => {
    if (stream == null) {
        return (
            <span style={{ fontSize: '0.85em', color: '#999' }}>
                {showLabel ? 'Stream: ' : ''}—
            </span>
        );
    }

    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            {showLabel && (
                <span style={{ fontSize: '0.85em', fontWeight: 'bold', color: '#666' }}>Stream:</span>
            )}
            <Label size={size} color="blue" title={stream.isDefault ? "The organisation's default stream" : undefined}>
                <Icon name="feed" />
                {stream.name}
            </Label>
        </span>
    );
};

/** The fields `StreamBadge` needs, for splicing into a post selection set. */
export const STREAM_BADGE_FIELDS = 'stream { id name isDefault }';
